import { useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { eventSuffix } from "./calUtils";

function Result({ r }) {
  return (
    <div className={`rounded-md border p-4 ${r.is_source ? "border-[#F59E0B]/50" : "border-[#2D3442]"}`} data-testid={`convert-result-${r.calendar_id}`}>
      <div className="flex items-center gap-2 text-sm text-[#A8A090]"><span className="h-2 w-2 rounded-full" style={{ background: r.color }} />{r.name}{r.is_source && " · source"}</div>
      <div className="mt-1 font-display text-xl text-[#F3EBDD]">{r.out_of_range ? "Outside this calendar's range" : r.label}</div>
      {r.days_spanned > 1 && <div className="text-xs text-[#A8A090]">The source day spans {r.days_spanned} local days here</div>}
      {r.t != null && <div className="font-mono-lf text-xs text-[#C4B5FD]">Timeline T{r.t}</div>}
      {r.moons?.length > 0 && <div className="mt-1 text-xs text-[#A8A090]">{r.moons.map((m) => `${m.name}: ${m.phase}`).join(" · ")}</div>}
      {r.events?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{r.events.map((e, i) => <span key={i} className="rounded px-2 py-0.5 text-xs" style={{ background: `${e.color}22`, color: e.color }}>{e.title}{eventSuffix(e)}</span>)}</div>}
    </div>
  );
}

export const ConverterPanel = ({ uid, calendars, initial }) => {
  const [form, setForm] = useState({ calendar_id: initial.id, year: initial.today.year, month: initial.today.month, day: initial.today.day });
  const [res, setRes] = useState(null);
  const cal = calendars.find((c) => c.id === form.calendar_id) || initial;
  const run = async () => {
    try {
      const { data } = await api.post(`/universes/${uid}/calendars/convert`, { ...form, year: parseInt(form.year, 10), day: parseInt(form.day, 10) || 1 });
      setRes(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-6" data-testid="converter-panel">
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-[#2D3442] bg-[#13171F]/60 p-4">
        <div className="space-y-1.5"><Label>Calendar</Label>
          <Select value={form.calendar_id} onValueChange={(v) => setForm({ ...form, calendar_id: v, month: 0, day: 1 })}>
            <SelectTrigger className="w-56" data-testid="convert-calendar-select"><SelectValue /></SelectTrigger>
            <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Day</Label><Input type="number" min={1} className="w-20 font-mono-lf" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="convert-day-input" /></div>
        <div className="space-y-1.5"><Label>Month</Label>
          <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: parseInt(v, 10) })}>
            <SelectTrigger className="w-44" data-testid="convert-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{cal.month_names.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Year</Label><Input type="number" className="w-28 font-mono-lf" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="convert-year-input" /></div>
        <Button onClick={run} data-testid="convert-run-button"><ArrowRightLeft className="mr-1.5 h-4 w-4" />Convert</Button>
      </div>
      {res && <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="convert-results">{res.results.map((r) => <Result key={r.calendar_id} r={r} />)}</div>}
    </div>
  );
};
